import { t } from "i18next";

import { TResourceWorkspace, TWorkspace } from "@/types";

type TNamedWorkspace = Pick<TWorkspace, "name" | "ownerName"> & {
	/** Mine: I own it. From `role` on /me, `mine` on a project or a client. */
	mine: boolean;
};

/** Its name, else whose it is: a workspace takes a name when someone gives it one on /workspace. */
export const workspaceLabel = (workspace: TNamedWorkspace): string => {
	if (workspace.name) return workspace.name;
	return workspace.mine
		? t("workspace.switcher.mine")
		: t("workspace.switcher.of", { name: workspace.ownerName });
};

/** The roles that add projects to a workspace (the API's canCreateInWorkspace). */
export const CREATING_ROLES: TWorkspace["role"][] = [
	"OWNER",
	"ADMIN",
	"EDITOR",
];

/** The roles that run a workspace: its clients, members, identity. */
export const MANAGING_ROLES: TWorkspace["role"][] = ["OWNER", "ADMIN"];

type TInWorkspace = { workspace?: TResourceWorkspace | null };

/** Items of one workspace; `workspace` null for items that carry none (the user's own). */
export type TWorkspaceGroup<T> = {
	workspace: TResourceWorkspace | null;
	items: T[];
};

/**
 * Items (projects, clients) by the workspace they belong to: the user's
 * own first, then the others by label. Each group keeps the items' order.
 */
export const groupByWorkspace = <T extends TInWorkspace>(
	items: T[]
): TWorkspaceGroup<T>[] => {
	const groups = new Map<number | "own", TWorkspaceGroup<T>>();
	for (const item of items) {
		const workspace = item.workspace ?? null;
		const key = !workspace || workspace.mine ? "own" : workspace.id;
		const group = groups.get(key) ?? { workspace, items: [] };
		group.items.push(item);
		groups.set(key, group);
	}
	const own = groups.get("own");
	const others = [...groups.entries()]
		.filter(([key]) => key !== "own")
		.map(([, group]) => group)
		.sort((a, b) =>
			workspaceLabel(a.workspace!).localeCompare(
				workspaceLabel(b.workspace!)
			)
		);
	return own ? [own, ...others] : others;
};

/**
 * A select's options across workspaces: flat for one workspace, else one
 * group per workspace under its label, the user's own first.
 */
export const toWorkspaceOptions = <T extends TInWorkspace, O>(
	items: T[],
	toOption: (item: T) => O
): O[] | { label: string; options: O[] }[] => {
	const groups = groupByWorkspace(items);
	if (groups.length < 2) return items.map(toOption);
	return groups.map((group) => ({
		label: workspaceLabel(
			group.workspace ?? { name: null, ownerName: "", mine: true }
		),
		options: group.items.map(toOption),
	}));
};
